/**
 * What the quiz funnel can actually offer in a given market.
 *
 * The quiz used to hardcode its product catalogue and consult neither
 * availability layer, so on a non-CH market it recommended MRI tiers the market
 * can't book and handed out booking deep-links for them. This module is the
 * quiz's adapter onto the existing single resolver
 * (`resolveServiceAvailability` in `lib/service-availability.ts`, AHD-7871),
 * which already composes the per-service CMS control plane with the
 * country/category booking lifecycle in a fixed precedence. We do NOT re-derive
 * either layer here — we map quiz ids onto service slugs + categories and let
 * the resolver answer.
 *
 * Resolved server-side and passed to the client wizard as a plain object, so it
 * has to stay serializable (no functions on `QuizMarket`).
 *
 * Worked example, NL at `NL_STATUS=checkup` — note the two layers disagree, and
 * that's the point:
 *   - `advanced-blood-test` → no CMS row, blood category open        → bookable
 *   - `full-body-mri`       → no CMS row, MRI category coming-soon   → coming_soon
 *   - `dexa-scan` &c.       → CMS row says `not_available`           → not_available
 * So NL keeps the MRI question (coming-soon → waitlist) but loses the
 * `mri_addons` step entirely, because all four of its products are
 * per-service unavailable. On DE those same four are `coming_soon`, so the step
 * renders. Reading only the CMS rows, or only the category gate, gets one of
 * these markets wrong.
 */

import {
  resolveServiceAvailability,
  type ServiceAvailability,
} from "@/lib/service-availability"
import { isBookingOpen, type ProductCategory } from "@/i18n/config"
import type { CmsServiceConfig } from "@/lib/cms"
import type { AddOnId, BaseTierId, Gender } from "./types"

interface ServiceRef {
  /** CMS service slugs this quiz product depends on. All must be offered. */
  slugs: string[]
  category: ProductCategory
}

/**
 * Base tier → the services it is composed of. `pro_mri` is the full-body MRI
 * plus brain volumetry, which is what distinguishes it from `advanced_mri`; if
 * brain volumetry isn't offered, Pro isn't a real product in that market.
 */
const TIER_SERVICES: Record<BaseTierId, ServiceRef> = {
  blood_only: { slugs: ["advanced-blood-test"], category: "blood" },
  advanced_mri: { slugs: ["full-body-mri"], category: "mri" },
  pro_mri: { slugs: ["full-body-mri", "brain-volumetry"], category: "mri" },
}

/**
 * Add-on → its service. `trend_overlay` is a free analysis layer with no
 * catalogue entry, so it has no availability of its own and is always offered.
 */
const ADDON_SERVICES: Record<AddOnId, ServiceRef | null> = {
  hormone_test: {
    slugs: ["hormone-blood-test-men", "hormone-blood-test-women"],
    category: "blood",
  },
  omega_test: { slugs: ["omega-3-6"], category: "blood" },
  b_vitamins: { slugs: ["essential-b-vitamins"], category: "blood" },
  trend_overlay: null,
  calcium_score: { slugs: ["calcium-score"], category: "mri" },
  dexa_scan: { slugs: ["dexa-scan"], category: "mri" },
  mamma_mri: { slugs: ["mamma-mri"], category: "mri" },
}

/** Gender-specific hormone slug, so a per-gender divergence resolves exactly. */
function hormoneSlug(gender: Gender | undefined): string {
  return gender === "female"
    ? "hormone-blood-test-women"
    : "hormone-blood-test-men"
}

/**
 * Precedence when a quiz product spans several services: the *least* available
 * state wins, because the composite can't be sold without all its parts. Order
 * mirrors `resolveServiceAvailability`'s own precedence.
 */
const SEVERITY: ServiceAvailability[] = [
  "hidden",
  "not_available",
  "waitlist",
  "coming_soon",
  "bookable",
]

function leastAvailable(states: ServiceAvailability[]): ServiceAvailability {
  for (const state of SEVERITY) {
    if (states.includes(state)) return state
  }
  return "bookable"
}

export interface QuizMarket {
  country: string
  /** Currency code for price display + the completion payload. */
  currency: string
  tiers: Record<BaseTierId, ServiceAvailability>
  /**
   * Add-on availability. `hormone_test` is the least-available of the men's and
   * women's tests; use {@link addOnAvailability} once gender is known for the
   * exact per-gender answer.
   */
  addOns: Record<AddOnId, ServiceAvailability>
  /** Per-gender hormone availability, since the two are separate CMS services. */
  hormoneByGender: Record<Gender, ServiceAvailability>
  /**
   * Whether to render the insurer step, the coverage band and the pro-forma
   * request. CH-only: there is no Dutch insurance story, and DE is a pure
   * self-pay market.
   */
  insurance: boolean
  /**
   * Whether this market sells Ahead Membership. When false, add-on rows must not
   * advertise a member price — on a `checkup` market membership is an expansion
   * product that isn't bookable, so "memberprijs" would dangle a discount the
   * visitor can't buy.
   */
  membership: boolean
}

export function buildQuizMarket(args: {
  country: string
  currency: string
  servicesConfig: CmsServiceConfig[]
}): QuizMarket {
  const { country, currency, servicesConfig } = args

  const resolve = (ref: ServiceRef): ServiceAvailability =>
    leastAvailable(
      ref.slugs.map((pathOrSlug) =>
        resolveServiceAvailability({
          country,
          category: ref.category,
          pathOrSlug,
          servicesConfig,
        })
      )
    )

  const tiers = {} as Record<BaseTierId, ServiceAvailability>
  for (const [tier, ref] of Object.entries(TIER_SERVICES)) {
    tiers[tier as BaseTierId] = resolve(ref)
  }

  const addOns = {} as Record<AddOnId, ServiceAvailability>
  for (const [id, ref] of Object.entries(ADDON_SERVICES)) {
    addOns[id as AddOnId] = ref ? resolve(ref) : "bookable"
  }

  const hormoneRef = ADDON_SERVICES.hormone_test!
  const hormoneByGender: Record<Gender, ServiceAvailability> = {
    male: resolve({
      slugs: [hormoneSlug("male")],
      category: hormoneRef.category,
    }),
    female: resolve({
      slugs: [hormoneSlug("female")],
      category: hormoneRef.category,
    }),
  }

  return {
    country,
    currency,
    tiers,
    addOns,
    hormoneByGender,
    insurance: country === "ch",
    membership: isBookingOpen(country, "membership"),
  }
}

/** Availability of an add-on, exact for gender-specific ones. */
export function addOnAvailability(
  market: QuizMarket,
  id: AddOnId,
  gender: Gender | undefined
): ServiceAvailability {
  if (id === "hormone_test" && gender) return market.hormoneByGender[gender]
  return market.addOns[id]
}

/**
 * Whether the funnel should mention this product at all. `not_available` and
 * `hidden` products are dropped from questions and recommendations;
 * `coming_soon` / `waitlist` ones are kept and routed to the waitlist (D3).
 */
export function isOffered(state: ServiceAvailability): boolean {
  return state !== "not_available" && state !== "hidden"
}

/** Whether a booking deep-link is valid, as opposed to a waitlist CTA. */
export function isBookable(state: ServiceAvailability): boolean {
  return state === "bookable"
}

export function isTierOffered(market: QuizMarket, tier: BaseTierId): boolean {
  return isOffered(market.tiers[tier])
}

export function isAddOnOffered(
  market: QuizMarket,
  id: AddOnId,
  gender?: Gender
): boolean {
  return isOffered(addOnAvailability(market, id, gender))
}

/** The imaging follow-ups behind the `mri_addons` step. */
export const MRI_ADDON_IDS: AddOnId[] = [
  "calcium_score",
  "dexa_scan",
  "mamma_mri",
]

/**
 * Whether the `mri_addons` step has anything to show. Brain interest is asked
 * on that step too (it drives the Pro tier rather than being an add-on), so the
 * step survives if brain volumetry alone is offered.
 */
export function hasMriAddons(market: QuizMarket): boolean {
  return (
    isTierOffered(market, "pro_mri") ||
    MRI_ADDON_IDS.some((id) => isAddOnOffered(market, id))
  )
}

/** Whether the MRI question is worth asking — i.e. any MRI tier is offered. */
export function hasMri(market: QuizMarket): boolean {
  return (
    isTierOffered(market, "advanced_mri") || isTierOffered(market, "pro_mri")
  )
}
